import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import type { Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

import {
  TABLE_COLUMN_HANDLE_TOP_OFFSET,
  TABLE_CONTROL_GAP,
  TABLE_CONTROL_SIZE,
  TABLE_ROW_HANDLE_SIZE,
} from './constants';
import type {
  WysiwygTableAction,
  WysiwygTableControls,
  WysiwygTableMenu,
} from './types';

interface WysiwygTableWorkflowOptions {
  getEditor: () => Editor | undefined;
  getVisualSurfaceElement: () => HTMLDivElement | undefined;
  getIsHtmlMode: () => boolean;
  getHasFocus: () => boolean;
  closeDropdownMenu: () => void;
  clearLinkContext: () => void;
  syncEditorBodyHeight: () => void;
}

export default class WysiwygTableWorkflow {
  private activeTableCellElement?: HTMLTableCellElement;

  @tracked
  activeTableControls: WysiwygTableControls | null = null;

  @tracked
  activeTableMenu: WysiwygTableMenu = null;

  constructor(private options: WysiwygTableWorkflowOptions) {}

  get shouldShowTableControls() {
    return Boolean(
      this.activeTableControls &&
        !this.options.getIsHtmlMode() &&
        this.options.getHasFocus()
    );
  }

  get tableControlsStyle() {
    const controls = this.activeTableControls;

    if (!controls) {
      return htmlSafe('');
    }

    return htmlSafe(
      [
        `--wysiwyg-table-left: ${controls.tableLeft}px`,
        `--wysiwyg-table-top: ${controls.tableTop}px`,
        `--wysiwyg-table-width: ${controls.tableWidth}px`,
        `--wysiwyg-table-height: ${controls.tableHeight}px`,
        `--wysiwyg-table-row-top: ${controls.rowTop}px`,
        `--wysiwyg-table-row-height: ${controls.rowHeight}px`,
        `--wysiwyg-table-column-left: ${controls.columnLeft}px`,
        `--wysiwyg-table-column-width: ${controls.columnWidth}px`,
      ].join('; ')
    );
  }

  get showTableRowMenu() {
    return this.activeTableMenu === 'row';
  }

  get showTableColumnMenu() {
    return this.activeTableMenu === 'column';
  }

  get canDeleteActiveTableRow() {
    return (this.activeTableControls?.rowCount ?? 0) > 1;
  }

  get canDeleteActiveTableColumn() {
    return (this.activeTableControls?.columnCount ?? 0) > 1;
  }

  clear() {
    this.activeTableControls = null;
    this.activeTableCellElement = undefined;
    this.activeTableMenu = null;
  }

  @action
  closeMenu() {
    this.activeTableMenu = null;
  }

  @action
  closeMenuAndRemeasure() {
    this.closeMenu();
    this.syncActiveTableControls(this.activeTableCellElement);
  }

  syncActiveTableControls(cell = this.getTableCellFromSelection()) {
    if (!cell || this.options.getIsHtmlMode()) {
      if (!this.activeTableMenu) {
        this.activeTableControls = null;
        this.activeTableCellElement = undefined;
      }

      return;
    }

    const row = cell.parentElement;
    const table = cell.closest('table');
    const visualSurface = this.options.getVisualSurfaceElement();

    /* istanbul ignore if -- Table controls are synced from rendered table cells inside the visual surface. */
    if (
      !(row instanceof HTMLTableRowElement) ||
      !(table instanceof HTMLTableElement) ||
      !visualSurface
    ) {
      return;
    }

    const tableRect = table.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const surfaceRect = visualSurface.getBoundingClientRect();
    const toSurfaceX = (value: number) =>
      value - surfaceRect.left + visualSurface.scrollLeft;
    const toSurfaceY = (value: number) =>
      value - surfaceRect.top + visualSurface.scrollTop;
    const firstRow = table.rows[0];

    this.activeTableCellElement = cell;
    this.activeTableControls = {
      tableLeft: toSurfaceX(tableRect.left),
      tableTop: toSurfaceY(tableRect.top),
      tableWidth: tableRect.width,
      tableHeight: tableRect.height,
      rowTop: toSurfaceY(rowRect.top),
      rowHeight: rowRect.height,
      columnLeft: toSurfaceX(cellRect.left),
      columnWidth: cellRect.width,
      rowCount: table.rows.length,
      columnCount: firstRow?.cells.length ?? 0,
    };
  }

  @action
  handleVisualSurfaceMouseMove(event: MouseEvent) {
    if (this.activeTableMenu || this.options.getIsHtmlMode()) {
      return;
    }

    const cell = this.getTableCellFromEvent(event);

    if (cell) {
      if (this.shouldSyncTableCell(cell)) {
        this.syncActiveTableControls(cell);
      }

      return;
    }

    if (
      this.getTableFromEvent(event) ||
      this.isTableControlEvent(event) ||
      this.isActiveTableHoverAreaEvent(event)
    ) {
      return;
    }

    this.clearActiveTableControls();
  }

  @action
  handleVisualSurfaceMouseLeave() {
    this.clearActiveTableControls();
  }

  @action
  addTableRowBelow(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.runTableAction('insert-row-below');
  }

  @action
  addTableColumnRight(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.runTableAction('insert-column-right');
  }

  @action
  openTableMenu(menu: Exclude<WysiwygTableMenu, null>, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.options.closeDropdownMenu();
    this.options.clearLinkContext();
    this.activeTableMenu = this.activeTableMenu === menu ? null : menu;
  }

  @action
  performTableAction(action: WysiwygTableAction, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.runTableAction(action);
  }

  private getElementFromEvent(event: Event) {
    const target = event.target;

    return target instanceof Element
      ? target
      : target instanceof Text
      ? target.parentElement
      : null;
  }

  private getTableCellFromEvent(event: Event) {
    const targetElement = this.getElementFromEvent(event);
    const cell = targetElement?.closest('td, th');
    const editorElement = this.options.getEditor()?.view.dom;

    if (
      !(cell instanceof HTMLTableCellElement) ||
      !(editorElement instanceof HTMLElement) ||
      !editorElement.contains(cell)
    ) {
      return null;
    }

    return cell;
  }

  private getTableFromEvent(event: Event) {
    const targetElement = this.getElementFromEvent(event);
    const table = targetElement?.closest('table');
    const editorElement = this.options.getEditor()?.view.dom;

    if (
      !(table instanceof HTMLTableElement) ||
      !(editorElement instanceof HTMLElement) ||
      !editorElement.contains(table)
    ) {
      return null;
    }

    return table;
  }

  private isTableControlEvent(event: Event) {
    return Boolean(
      this.getElementFromEvent(event)?.closest(
        '.wysiwyg-editor-shell__table-controls'
      )
    );
  }

  private isActiveTableHoverAreaEvent(event: MouseEvent) {
    const controls = this.activeTableControls;
    const visualSurface = this.options.getVisualSurfaceElement();

    if (
      !controls ||
      !visualSurface ||
      (event.clientX === 0 && event.clientY === 0)
    ) {
      return false;
    }

    const surfaceRect = visualSurface.getBoundingClientRect();
    const x = event.clientX - surfaceRect.left + visualSurface.scrollLeft;
    const y = event.clientY - surfaceRect.top + visualSurface.scrollTop;

    return (
      x >= controls.tableLeft - TABLE_ROW_HANDLE_SIZE &&
      x <=
        controls.tableLeft +
          controls.tableWidth +
          TABLE_CONTROL_GAP +
          TABLE_CONTROL_SIZE &&
      y >= controls.tableTop - TABLE_COLUMN_HANDLE_TOP_OFFSET &&
      y <=
        controls.tableTop +
          controls.tableHeight +
          TABLE_CONTROL_GAP +
          TABLE_CONTROL_SIZE
    );
  }

  private clearActiveTableControls() {
    if (this.activeTableMenu) {
      return;
    }

    this.activeTableControls = null;
    this.activeTableCellElement = undefined;
  }

  private shouldSyncTableCell(cell: HTMLTableCellElement) {
    return !this.activeTableControls || cell !== this.activeTableCellElement;
  }

  private getTableCellFromSelection() {
    const editor = this.options.getEditor();

    /* istanbul ignore if -- Table selection syncing only runs after the editor is mounted. */
    if (!editor) {
      return null;
    }

    const { selection } = editor.state;
    const domAtSelection = editor.view.domAtPos(selection.from);
    const node = domAtSelection.node;
    const targetElement =
      node instanceof Element
        ? node
        : node instanceof Text
        ? node.parentElement
        : null;
    const cell = targetElement?.closest('td, th');

    return cell instanceof HTMLTableCellElement ? cell : null;
  }

  private focusActiveTableCell() {
    const editor = this.options.getEditor();
    const cell = this.activeTableCellElement;

    /* istanbul ignore if -- Table actions are only available after hovering a rendered table cell. */
    if (!editor || !cell) {
      return false;
    }

    const rawPosition = editor.view.posAtDOM(cell, 0);
    const position = Math.min(
      Math.max(rawPosition + 1, 1),
      editor.state.doc.content.size
    );
    const transaction = editor.state.tr.setSelection(
      TextSelection.near(editor.state.doc.resolve(position))
    );

    editor.view.dispatch(transaction);
    editor.view.focus();

    return true;
  }

  private runTableAction(action: WysiwygTableAction) {
    const editor = this.options.getEditor();

    /* istanbul ignore if -- Table action buttons only render while an active table cell exists. */
    if (!editor || !this.focusActiveTableCell()) {
      return;
    }

    switch (action) {
      case 'insert-row-above':
        editor.chain().focus().addRowBefore().run();
        break;
      case 'insert-row-below':
        editor.chain().focus().addRowAfter().run();
        break;
      case 'delete-row':
        if (this.canDeleteActiveTableRow) {
          editor.chain().focus().deleteRow().run();
        }
        break;
      case 'insert-column-left':
        editor.chain().focus().addColumnBefore().run();
        break;
      case 'insert-column-right':
        editor.chain().focus().addColumnAfter().run();
        break;
      case 'delete-column':
        if (this.canDeleteActiveTableColumn) {
          editor.chain().focus().deleteColumn().run();
        }
        break;
    }

    this.activeTableMenu = null;

    next(() => {
      this.syncActiveTableControls();
      this.options.syncEditorBodyHeight();
    });
  }
}
